/**
 * Location row. List mode is one collapsed line; five part fields render
 * only while that row is being edited. Merge-review (no onToggleEdit) still
 * shows parts so the review panel can compare keep vs twin.
 */
import type { ReactNode } from 'react';

import type { AddressParts } from '../sections/locationMerge';
import type { LocationMergeCandidate } from '../sections/LocationMergePanel';

import { composedLocationLine } from './locationDisplay';
import { PartField } from './PartField';

function KindChip({
  tone,
  children,
}: {
  tone: 'verified' | 'unverified';
  children: ReactNode;
}) {
  const styles: Record<string, string> = {
    verified:
      'border-transparent bg-[hsl(var(--status-success))] text-[hsl(var(--status-success-foreground))]',
    unverified: 'border-transparent bg-muted text-foreground',
  };
  return (
    <span
      className={[
        'inline-flex items-center rounded-[9px] border px-1.5 py-0.5 text-[11px] font-medium leading-none',
        styles[tone],
      ].join(' ')}
    >
      {children}
    </span>
  );
}

function Part({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value?.trim() ? value : '—'}</dd>
    </div>
  );
}

const REMOVE_BTN_CLASS = [
  'inline-flex min-h-8 items-center justify-center rounded-[9px] bg-destructive px-2.5',
  'text-[11px] font-medium text-destructive-foreground hover:opacity-90',
  'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
  // L9 #13: 44px reach via 6px hit-slop on the 32px visual box (Yablonski
  // Table 2-1 p18) without inflating the one-line row.
  "relative after:absolute after:content-[''] after:-inset-1.5",
].join(' ');

export type LocationPartField = keyof AddressParts;

export function MergeLocationRow({
  location,
  dimmed,
  isPrimary,
  editing,
  onToggleEdit,
  onPromote,
  onUpdateField,
  editHint,
  fieldsDisabled,
  invalidFields,
  onRemove,
  removeDisabled,
  removeDisabledReason,
  primaryDetails,
  rowDetails,
}: {
  location: LocationMergeCandidate;
  dimmed?: boolean;
  isPrimary?: boolean;
  editing?: boolean;
  onToggleEdit?: () => void;
  onPromote?: () => void;
  /** Any field blur sends the full five-part snapshot from the form. */
  onUpdateField?: (parts: AddressParts) => void;
  /** Optional hint line rendered inside the edit panel (draft routing,
   *  incomplete-state) — plain feedback text, never a second field. */
  editHint?: ReactNode;
  /** L9 #14: disable the part inputs while the draft POST is in flight
   *  (Doherty — feedback during processing, no double-submit). */
  fieldsDisabled?: boolean;
  /** L9 #11: fields whose missing value blocked the save — rendered with a
   *  destructive border so the no-op is visible. */
  invalidFields?: LocationPartField[];
  onRemove?: () => void;
  removeDisabled?: boolean;
  removeDisabledReason?: string;
  primaryDetails?: ReactNode;
  /** Per-row Radius/Tracking/GBP/Hours (+ contact fallback). List-edit only. */
  rowDetails?: ReactNode;
}) {
  const rating = location.enrichment?.gbp_rating;
  const gbpOn = Boolean(location.gbpUrl?.trim());
  const trackingOn = Boolean(
    location.trackingPhone?.trim() ||
      (Array.isArray(location.enrichment?.tracking) && location.enrichment.tracking.length > 0),
  );
  const showEnrichment = gbpOn || rating != null || trackingOn;
  const showPrimaryMark = Boolean(isPrimary) && location.locationKey !== 'Primary';
  const listMode = Boolean(onToggleEdit);
  const displayName = location.locationKey;
  const composed = composedLocationLine(location.parts, location.displayAddress);

  function commitField(field: LocationPartField, value: string) {
    if (!onUpdateField) return;
    const read = (name: LocationPartField) => {
      if (name === field) return value;
      const el = document.getElementById(
        `bb-location-${location.id}-${name}`,
      ) as HTMLInputElement | null;
      return el?.value ?? location.parts[name] ?? '';
    };
    onUpdateField({
      street: read('street'),
      suite: read('suite'),
      city: read('city'),
      state: read('state'),
      zip: read('zip'),
    });
  }

  const partsGrid = onUpdateField ? (
    <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
      {([
        ['Street', 'street'],
        ['Suite', 'suite'],
        ['City', 'city'],
        ['State', 'state'],
        ['ZIP', 'zip'],
      ] as const).map(([label, field]) => (
        <PartField
          key={field}
          label={label}
          inputId={`bb-location-${location.id}-${field}`}
          value={location.parts[field] ?? ''}
          invalid={invalidFields?.includes(field)}
          disabled={fieldsDisabled}
          onCommit={(v) => commitField(field, v)}
        />
      ))}
    </div>
  ) : (
    <dl className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-5">
      <Part label="Street" value={location.parts.street} />
      <Part label="Suite" value={location.parts.suite} />
      <Part label="City" value={location.parts.city} />
      <Part label="State" value={location.parts.state} />
      <Part label="ZIP" value={location.parts.zip} />
    </dl>
  );

  if (!listMode) {
    return (
      <div
        data-testid={`bb-essentials-loc-merge-row-${location.id}`}
        className={['rounded-[9px] border border-border bg-card p-3', dimmed ? 'opacity-60' : ''].join(
          ' ',
        )}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-sm font-medium text-foreground">{location.locationKey}</span>
          <div className="flex items-center gap-1.5">
            {location.verified ? (
              <KindChip tone="verified">Verified</KindChip>
            ) : (
              <KindChip tone="unverified">Unverified</KindChip>
            )}
            {showPrimaryMark && (
              <span className="text-[11px] font-medium text-muted-foreground">Primary</span>
            )}
            {!isPrimary && onRemove && (
              <button
                type="button"
                data-testid={`bb-location-remove-${location.id}`}
                disabled={removeDisabled}
                title={removeDisabledReason}
                onClick={onRemove}
                className={REMOVE_BTN_CLASS}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        {partsGrid}
        {showEnrichment && (
          <div className="mt-2.5 flex items-center gap-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
            <span>GBP: {gbpOn ? 'linked' : 'none'}</span>
            <span>Rating: {rating ?? '—'}</span>
            <span>Tracking: {trackingOn ? 'on' : 'off'}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={`bb-essentials-loc-merge-row-${location.id}`}
      className={dimmed ? 'opacity-60' : undefined}
    >
      {/* L9 #15 (Lupton #4, p226): every text element on the row locks to
          one baseline — 17-step header excluded (it is the card title, above
          the rows), but name/address/badge/actions share a baseline. */}
      <div className="flex items-baseline gap-3 px-4 py-3">
        <button
          type="button"
          data-testid={`bb-location-star-${location.id}`}
          aria-pressed={Boolean(isPrimary)}
          aria-label={
            isPrimary
              ? `${displayName} is your primary location`
              : `Make ${displayName} your primary location`
          }
          disabled={!onPromote || isPrimary}
          onClick={onPromote}
          title={isPrimary ? 'Primary location' : 'Make primary'}
          className={[
            // L9 #13: 24×24 visual box, 15px glyph, 10px hit-slop → 44×44
            // reach (Yablonski Ch2 Table 2-1 p18 — star is the most-clicked
            // control on the card).
            "relative grid h-6 w-6 flex-none place-items-center border-0 bg-transparent p-0 text-[15px] leading-none after:absolute after:content-[''] after:-inset-[10px]",
            isPrimary ? 'text-coral' : 'text-muted-foreground',
            onPromote && !isPrimary ? 'cursor-pointer' : 'cursor-default',
          ].join(' ')}
        >
          {isPrimary ? '★' : '☆'}
        </button>
        <span className="flex-none text-sm font-semibold text-foreground">{displayName}</span>
        <span className="min-w-0 flex-1 truncate text-[13px] text-teal-dark">
          {composed || 'No address yet'}
        </span>
        {location.verified ? (
          <KindChip tone="verified">Verified</KindChip>
        ) : (
          <KindChip tone="unverified">Unverified</KindChip>
        )}
        <button
          type="button"
          data-testid={`bb-location-edit-${location.id}`}
          aria-expanded={Boolean(editing)}
          onClick={onToggleEdit}
          className={[
            'flex-none border-0 bg-transparent text-[13px] text-muted-foreground hover:text-foreground',
            // L9 #13: 32px visual box + 6px hit-slop → 44px reach.
            "inline-flex min-h-8 items-center px-2.5 relative after:absolute after:content-[''] after:-inset-1.5",
          ].join(' ')}
        >
          Edit
        </button>
        {!isPrimary && onRemove && (
          <button
            type="button"
            data-testid={`bb-location-remove-${location.id}`}
            disabled={removeDisabled}
            title={removeDisabledReason}
            onClick={onRemove}
            className={`flex-none ${REMOVE_BTN_CLASS}`}
          >
            Remove
          </button>
        )}
      </div>
      {editing && onUpdateField && (
        <div
          data-testid={`bb-location-edit-panel-${location.id}`}
          className="border-t border-border bg-coral-light px-4 py-3"
        >
          {partsGrid}
          {editHint && <p className="mt-2 text-[11px] text-foreground/70">{editHint}</p>}
          {rowDetails}
        </div>
      )}
      {isPrimary && primaryDetails}
    </div>
  );
}
